import { type FileRecord } from "@/shared/domain/files";
import { ingestPhase, readVisualizationAsset } from "@/shared/domain/urdf";

/**
 * What picking a listed file does, and what to say about the state it is in.
 *
 * The reason is shown on the row rather than used to hide it. A user who knows
 * `robot.urdf` is in the dataset and cannot find it in the picker has been lied
 * to; one who finds it reading "Converting…" has been told something true and
 * actionable.
 *
 * ## Why `canIngest` is a parameter and not a second call site
 *
 * Starting a conversion is a write, and writes are behind `release-3d-assets`.
 * The picker is reachable without that flag: `MeshConfig` keeps the Model mesh
 * type offered for an entity that *already has* a model, so a flag-off user
 * editing a workspace someone else authored can still open this list to swap
 * one converted model for another. That path must not gain the ability to
 * launch a conversion, so the states whose pick would start one degrade to
 * unpickable — keeping their captions, which are true either way.
 */
export interface ModelReadiness {
  /**
   * What picking this row does, or `null` when picking it can do nothing.
   *
   * - `load` — an artifact exists; resolve it and hand it back
   * - `ingest` — there is no artifact to load, so the pick opens the ingest
   *   wizard on this file and the model comes back from there
   */
  pick: "load" | "ingest" | null;
  /**
   * What to say instead of the row's size-and-date caption.
   *
   * Only set when the state needs explaining — a ready file keeps the default.
   */
  reason?: string;
}

const READY: ModelReadiness = { pick: "load" };

/**
 * The row state for one file in the model picker.
 *
 * Pure, and computed from the record the file query already returned: the
 * status block and `upload_id` are both on it, so every row's state costs
 * nothing beyond the listing itself.
 *
 * ## Why this is not a server-side filter
 *
 * The status half of the test is expressible as a query condition, but the
 * other half is not: "current" means `block.source_upload_id ===
 * record.upload_id`, a column-to-column comparison the query language has no
 * form for. A completed-but-stale URDF passes any filter that can be written,
 * so this pass has to exist regardless — and a server condition on top of it
 * would be a second source of truth for something already recomputed here.
 *
 * ## The states
 *
 * Every one of them is a statement about a conversion, because every row the
 * picker lists is an ingestable source — a robot description or a point cloud
 * scan. A `.gltf` used to short-circuit this as "already a model, nothing to
 * convert"; the picker no longer offers one, so that case is gone rather than
 * kept as an unreachable branch. A glTF reaching
 * here now reads as un-ingested, which is the truthful answer to "does an
 * ingested asset back this file" — it does not.
 *
 * - `preview` — an artifact built from the bytes this file currently holds
 * - `converting` — the worker is running; the artifact is not there yet, and
 *   asking for another run would not make one appear sooner. The only row with
 *   nothing to offer.
 * - `failed` — the run stopped, and the only way on is to run it again, which
 *   is what the wizard's own failed step offers
 * - `settings` — never ingested, **or** ingested from a since-replaced upload.
 *   Both mean "no artifact this file derives", which is the same non-choice
 *   from the picker's side, so both get the same way out of it: picking the row
 *   launches the ingest wizard, and a stale asset is re-converted exactly as a
 *   never-converted one is converted.
 */
export function modelReadiness(
  record: FileRecord,
  canIngest: boolean,
): ModelReadiness {
  switch (ingestPhase(readVisualizationAsset(record), record.upload_id)) {
    case "preview":
      return READY;
    case "converting":
      return { pick: null, reason: "Converting…" };
    case "failed":
      return canIngest
        ? { pick: "ingest", reason: "Conversion failed" }
        : { pick: null, reason: "Conversion failed" };
    case "settings":
      return canIngest
        ? { pick: "ingest", reason: "Not ingested yet" }
        : { pick: null, reason: "Not ingested yet" };
  }
}
